import { useReducer, type ReactNode } from 'react';

export const stopPropagation = (evt: { stopPropagation: () => void }) => {
  evt.stopPropagation();
};

export const preventDefault = (evt: { preventDefault: () => void }) => {
  evt.preventDefault();
};

type Entry<T extends Record<string, string>> = [key: keyof T, value: string];
type Action<T extends Record<string, string>> =
  | keyof T
  | Partial<T>
  | Entry<T>
  | Entry<T>[];
const EMPTY_RECORD = {};
const stringRecordReducer = <T extends Record<string, string>>(
  state: Partial<T>,
  action: Action<T>,
) => {
  if (Array.isArray(action)) {
    if (Array.isArray(action[0])) {
      const updateManyEntries = action as Entry<T>[];

      return updateManyEntries.reduce((result, [key, value]) => {
        return {
          ...result,
          [key]: value,
        };
      }, state);
    } else {
      const updateOneEntry = action as Entry<T>;
      const [key, value] = updateOneEntry;
      return {
        ...state,
        [key]: value,
      };
    }
  } else {
    if (typeof action === 'object') {
      //set value
      return action;
    } else {
      //clear key
      return Object.fromEntries(
        Object.entries(state).filter(([k, _v]) => k !== action),
      ) as T;
    }
  }
};

export const useStringRecord = <T extends Record<string, string>>() => {
  const [value, dispatch] = useReducer<Partial<T>, [Action<T>]>(
    stringRecordReducer,
    EMPTY_RECORD as T,
  );

  return {
    value,
    empty: Object.keys(value).length === 0,
    update: (v: Entry<T> | Entry<T>[]) => {
      dispatch(v);
    },
    clear: (k?: keyof T) => {
      if (k) {
        dispatch(k);
      } else {
        dispatch(EMPTY_RECORD as T);
      }
    },
    set: (v: T) => {
      dispatch(v);
    },
  };
};

export type ChildrenProps = {
  children?: ReactNode;
};
export type ClassNameProps = {
  className?: string;
};
